import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface SupportTicketMessage {
  messageId: string;
  userId: string;
  message: string;
  isAdmin: boolean;
  attachments?: string[];
  createdAt: string;
}

export interface SupportTicket {
  ticketId: string;
  userId: string;
  type: 'technical' | 'billing' | 'account' | 'general' | 'booking_issue';
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  bookingId?: string;
  attachments?: string[];
  messages?: SupportTicketMessage[];
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface SupportTicketWithUser extends SupportTicket {
  user?: {
    userId: string;
    email: string;
    name?: string;
    phone?: string;
  };
}

export interface SupportTicketListResponse {
  tickets: SupportTicket[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListSupportTicketsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  status?: string;
  type?: string;
  priority?: string;
  userId?: string;
}

class SupportTicketManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listSupportTickets(params?: ListSupportTicketsParams): Promise<SupportTicketListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.userId) queryParams.append('userId', params.userId);

    const response = await this.api.get<{ data: SupportTicketListResponse }>(
      `/admin/support-tickets?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async getSupportTicket(ticketId: string): Promise<SupportTicketWithUser> {
    const response = await this.api.get<{ data: { ticket: SupportTicket; user?: SupportTicketWithUser['user'] } }>(
      `/admin/support-tickets/${ticketId}`
    );
    return {
      ...response.data.data.ticket,
      user: response.data.data.user,
    };
  }

  async replyToTicket(ticketId: string, message: string): Promise<SupportTicket> {
    const response = await this.api.post<{ data: { ticket: SupportTicket } }>(
      `/admin/support-tickets/${ticketId}/reply`,
      { message }
    );
    return response.data.data.ticket;
  }

  async updateTicket(
    ticketId: string,
    updates: {
      status?: SupportTicket['status'];
      priority?: SupportTicket['priority'];
      assignedTo?: string;
    }
  ): Promise<SupportTicket> {
    const response = await this.api.put<{ data: { ticket: SupportTicket } }>(
      `/admin/support-tickets/${ticketId}`,
      updates
    );
    return response.data.data.ticket;
  }
}

export const supportTicketManagementService = new SupportTicketManagementService();

